import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faUser, faFileInvoice, faCalendarAlt, faHandshake, faFileAlt, faBox } from '@fortawesome/free-solid-svg-icons';
import invoicingService from '../../services/invoicingService';
import { dashboardService } from '../../services/dashboardService';
import { useUserInfo } from '../../contexts/UserContext';
import { resolveIssuerName } from '../../utils/issuerDisplay';
import InvoiceDatePicker from '../../components/ui/InvoiceDatePicker';
import { clearFieldError } from '../../components/Invoicing/InvoiceValidationModal';
import './OutgoingInvoice.css';

/**
 * Credit Note Screen 1 - Header Information
 * Per PM mockup:
 * - HMS Entity (from original invoice)
 * - Issuer (automatic)
 * - Invoice Template (from original invoice)
 * - Original Invoice Number (from original invoice)
 * - Credit Note Date (calendar)
 * - Internal Reference (3 fields from original invoice)
 * - Partner Name (from original invoice)
 * - Debtor Number (from original invoice)
 * - Customer Notes (multiline text, stored as supply_reference) + Show on the PDF tick (row 159)
 * - Credit Reason (dropdown - NEW)
 * - Credit Reason Notes (multiline - NEW)
 */

const CreditNoteScreen1 = ({ creditNoteData, setCreditNoteData, setLineItems, setVesselInfo, onNext, isReadOnly, issuerName = null, isPersisted = false }) => {
  const [templateTypes, setTemplateTypes] = useState([]);
  const [outgoingInvoices, setOutgoingInvoices] = useState([]);
  // null when the picker's list loaded. Otherwise why it did not — refused,
  // truncated, or broken. See loadDropdowns.
  const [invoiceLoadProblem, setInvoiceLoadProblem] = useState(null);
  const [sessionUserName, setSessionUserName] = useState('');
  
  // Get current user info
  const { userInfo, updateUserInfo } = useUserInfo();
  
  useEffect(() => {
    loadDropdowns();
    loadIssuerInfo();
  }, []);
  
  const loadIssuerInfo = async () => {
    try {
      // Try from context first
      if (userInfo.first_name && userInfo.last_name) {
        setSessionUserName(`${userInfo.first_name} ${userInfo.last_name}`.trim());
      } else {
        // Fetch from API if context is empty (page reload)
        const userInfoData = await dashboardService.getUserInfo();
        updateUserInfo(userInfoData);
        setSessionUserName(`${userInfoData.first_name} ${userInfoData.last_name}`.trim());
      }
    } catch (error) {
      console.error('Error loading issuer info:', error);
      setSessionUserName('Automatic (person creating the credit note)');
    }
  };

  // The Issuer names whoever RAISED this document, which is not whoever is
  // looking at it -- only an unsaved document belongs to the person typing.
  const issuerFullName = resolveIssuerName({
    isPersisted,
    storedIssuerName: issuerName,
    sessionUserName,
  });
  
  /**
   * Fill the two dropdowns this screen needs.
   *
   * The invoice list is loaded SEPARATELY from the templates, and its failure
   * is put on the screen. Both used to sit in one try/catch that logged to the
   * console and stopped — so a refusal, a broken connection or a half-loaded
   * page all produced the same thing: a picker with nothing in it. Finance then
   * reads "-- Select Invoice --" and no options and concludes there are no
   * approved invoices to credit, which is a different and much more expensive
   * answer than "we could not load them". An empty picker must only ever mean
   * empty.
   */
  const loadDropdowns = async () => {
    try {
      const templates = await invoicingService.fetchTemplateTypeOptions();
      setTemplateTypes(templates);
    } catch (error) {
      console.error('Error loading credit note templates:', error);
    }

    try {
      setInvoiceLoadProblem(null);
      // Load outgoing invoices for selection
      const invoices = await invoicingService.fetchOutgoingInvoices({ status: 'APPROVED,SENT,PAID' });
      setOutgoingInvoices(invoices.results || invoices);
    } catch (error) {
      console.error('Error loading invoices to credit:', error);
      setOutgoingInvoices([]);
      setInvoiceLoadProblem(
        invoicingService.describeListFailure(error, 'The invoices you can credit'),
      );
    }
  };
  
  const handleInvoiceSelect = async (invoiceId) => {
    if (!invoiceId) return;
    
    try {
      const prefillData = await invoicingService.createCreditNoteFromInvoice(invoiceId);
      
      // Update header data
      setCreditNoteData(prev => ({
        ...prev,
        ...prefillData
      }));
      
      // Update line items if provided
      if (prefillData.line_items && setLineItems) {
        setLineItems(prefillData.line_items);
      }
      
      // Update vessel info if provided
      if (prefillData.vessel_info && setVesselInfo) {
        setVesselInfo(prefillData.vessel_info);
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
    }
  };
  
  // Helper function to parse internal reference into separate parts
  const parseInternalReference = (internalRef) => {
    if (!internalRef) return { delivery: '', contracts: '', salesRecaps: '' };
    
    const lines = internalRef.split('\n').map(line => line.trim()).filter(line => line);
    let delivery = '';
    let contracts = [];
    let salesRecaps = '';
    
    lines.forEach(line => {
      if (line.startsWith('Delivery:')) {
        delivery = line.replace('Delivery:', '').trim();
      } else if (line.includes('Contract:')) {
        contracts.push(line.replace('Seller Contract:', '').replace('Buyer Contract:', '').trim());
      } else if (line.startsWith('Sales Recaps:')) {
        salesRecaps = line.replace('Sales Recaps:', '').trim();
      }
    });
    
    return {
      delivery,
      contracts: contracts.join(', '),
      salesRecaps
    };
  };

  // Rebuild internal_reference from the three editable sub-fields while
  // preserving any other lines. Uses "Seller Contract:" so the value
  // round-trips through parseInternalReference.
  const updateInternalReference = (field, value) => {
    setCreditNoteData(prev => {
      const current = parseInternalReference(prev.internal_reference);
      current[field] = value;
      const extras = (prev.internal_reference || '')
        .split('\n').map(l => l.trim()).filter(Boolean)
        .filter(l => !l.startsWith('Delivery:') && !l.includes('Contract:') && !l.startsWith('Sales Recaps:') && !l.startsWith('Purchase Recaps:'));
      const lines = [];
      if (current.delivery) lines.push(`Delivery: ${current.delivery}`);
      if (current.contracts) lines.push(`Seller Contract: ${current.contracts}`);
      if (current.salesRecaps) lines.push(`Sales Recaps: ${current.salesRecaps}`);
      return { ...prev, internal_reference: [...lines, ...extras].join('\n') };
    });
  };
  
  return (
    <>
      <div className="invoice-screen-content">
        <div className="invoice-form-section">
          <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <FontAwesomeIcon icon={faBuilding} /> Credit Note Header & Partner Information
            </span>
            <span style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'normal' }}>
              {creditNoteData.credit_note_number ? `Credit Note: ${creditNoteData.credit_note_number}` : '(number will be generated on save)'}
            </span>
          </h2>
          
          <div className="invoice-form-grid">
            {/* Left Column */}
            <div className="invoice-form-column">
              {/* Select Original Invoice */}
              <div className="form-field">
                <label className="field-label required">
                  <FontAwesomeIcon icon={faFileInvoice} /> Select Original Invoice
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {creditNoteData.original_invoice_number || 'N/A'}
                  </div>
                ) : (
                  <select
                    id="original_invoice_select"
                    className="form-select"
                    value={creditNoteData.original_invoice || ''}
                    onChange={(e) => {
                      handleInvoiceSelect(e.target.value);
                      clearFieldError('original_invoice_select');
                    }}
                  >
                    <option value="">-- Select Invoice --</option>
                    {outgoingInvoices.map(inv => (
                      <option key={inv.id} value={inv.id}>
                        {inv.invoice_number} - {inv.partner_name} - {inv.currency} {inv.gross_amount}
                      </option>
                    ))}
                  </select>
                )}
                {/* An empty picker with no explanation reads as "there is
                    nothing to credit". Say which it actually was. */}
                {invoiceLoadProblem && !isReadOnly && (
                  <div
                    role="alert"
                    className="field-load-problem"
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.6rem 0.8rem',
                      border: '1px solid #f59e0b',
                      borderLeft: '4px solid #b45309',
                      borderRadius: '4px',
                      background: '#fffbeb',
                      color: '#78350f',
                      fontSize: '0.85rem',
                    }}
                  >
                    <strong style={{ marginRight: '0.35rem' }}>
                      {invoiceLoadProblem.kind === 'truncated'
                        ? 'This picker is not showing every invoice.'
                        : 'The list of invoices could not be loaded.'}
                    </strong>
                    {invoiceLoadProblem.message}
                  </div>
                )}
              </div>
              
              {/* IMT Entity */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faBuilding} /> Entity
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={creditNoteData.hms_entity_name || ''}
                  readOnly
                  disabled
                  placeholder="from original invoice"
                />
              </div>
              
              {/* Issuer */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faUser} /> Issuer
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={issuerFullName}
                  readOnly
                  disabled
                />
              </div>
              
              {/* Invoice Template */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileInvoice} /> Invoice Template
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {templateTypes.find(t => t.value === creditNoteData.template_type)?.label || 'N/A'}
                  </div>
                ) : (
                  <select
                    id="template_type_select"
                    className="form-select"
                    value={creditNoteData.template_type || 'solids_eng'}
                    onChange={(e) => {
                      setCreditNoteData({ ...creditNoteData, template_type: e.target.value });
                      clearFieldError('template_type_select');
                    }}
                  >
                    {templateTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                )}
                <small className="field-hint">from original invoice</small>
              </div>
              
              {/* Original Invoice Number */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileInvoice} /> Original Invoice Number
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={creditNoteData.original_invoice_number || ''}
                  readOnly
                  disabled
                  placeholder="from original invoice"
                />
              </div>
              
              {/* Credit Note Date */}
              <div className="form-field">
                <InvoiceDatePicker
                  value={creditNoteData.invoice_date || ''}
                  onChange={(e) => {
                    setCreditNoteData({ ...creditNoteData, invoice_date: e.target.value });
                    clearFieldError('invoice_date');
                  }}
                  name="invoice_date"
                  id="invoice_date"
                  label={
                    <span>
                      <FontAwesomeIcon icon={faCalendarAlt} /> Credit Note Date
                    </span>
                  }
                  placeholder="Select credit note date"
                  required={true}
                  className="form-input"
                  readOnly={isReadOnly}
                />
              </div>
              
              {/* Internal Reference */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Internal Reference
                </label>
                <div className="internal-reference-group">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="delivery number"
                    value={parseInternalReference(creditNoteData.internal_reference).delivery}
                    onChange={(e) => updateInternalReference('delivery', e.target.value)}
                    readOnly={isReadOnly}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="contract numbers"
                    value={parseInternalReference(creditNoteData.internal_reference).contracts}
                    onChange={(e) => updateInternalReference('contracts', e.target.value)}
                    readOnly={isReadOnly}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="sales recap numbers"
                    value={parseInternalReference(creditNoteData.internal_reference).salesRecaps}
                    onChange={(e) => updateInternalReference('salesRecaps', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="invoice-form-column">
              {/* Partner Name */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faHandshake} /> Partner Name
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={creditNoteData.partner_name || ''}
                  readOnly
                  disabled
                  placeholder="from original invoice"
                />
              </div>
              
              {/* Debtor Number */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Debtor Number
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={creditNoteData.partner || ''}
                  readOnly
                  disabled
                  placeholder="from original invoice"
                />
              </div>
              
              {/* Customer Notes (stored as supply_reference), as on the invoice it corrects */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Customer Notes
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {creditNoteData.supply_reference || 'N/A'}
                  </div>
                ) : (
                  <textarea
                    className="form-textarea"
                    value={creditNoteData.supply_reference || ''}
                    onChange={(e) => setCreditNoteData({ ...creditNoteData, supply_reference: e.target.value })}
                    rows={3}
                    placeholder="Enter customer notes..."
                  />
                )}
                <label className="checkbox-label" style={{ marginTop: '6px' }}>
                  <input
                    type="checkbox"
                    checked={creditNoteData.supply_reference_visible_on_invoice || false}
                    onChange={(e) => setCreditNoteData({ ...creditNoteData, supply_reference_visible_on_invoice: e.target.checked })}
                    disabled={isReadOnly}
                  />
                  <span>Show on the PDF as "Customer Notes" (under Customer No.)</span>
                </label>
              </div>
              
              {/* Credit Reason */}
              <div className="form-field-flexible">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Credit Reason
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {creditNoteData.credit_reason_notes || 'N/A'}
                  </div>
                ) : (
                  <textarea
                    className="form-textarea"
                    value={creditNoteData.credit_reason_notes || ''}
                    onChange={(e) => setCreditNoteData({ ...creditNoteData, credit_reason_notes: e.target.value })}
                    rows={4}
                    placeholder="Detailed explanation of the credit (quality parameters, calculations, etc.)"
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <div className="screen-navigation" style={{ justifyContent: 'flex-end' }}>
            <button className="btn-primary" type="button" onClick={onNext}>
              Next: Products <FontAwesomeIcon icon={faBox} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreditNoteScreen1;
